// Registers the staff session (Authorization: Bearer, see backend/
// middleware/staffAuth.js) and, while a tenant has no staff passwords
// yet, the shared app password on the shared axios singleton, once, so
// every module's plain `axios.get/post/...` calls carry them without
// importing anything extra. Imported once from index.js for its side
// effect.
import axios from 'axios';
import { getSessionToken, setSessionToken } from './staffSessionStorage';
import { getAppPassword } from './appPasswordStorage';

// Every module calls axios.get('/api/...') with a relative path — fine in
// dev, where CRA's package.json "proxy" forwards it to localhost:3001, but
// that proxy has no effect on a production build. Once frontend/backend are
// separate deployed origins, relative paths need an absolute base to reach
// the right one; empty string preserves today's relative/proxy behavior
// when REACT_APP_API_URL isn't set.
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Fired when the backend says this browser is no longer signed in (session
// ended by inactivity, the 30-day cap, sign-out elsewhere or the login
// being switched off). AuthGate listens and shows the sign-in screen.
export const SIGNED_OUT_EVENT = 'optim:signed-out';

axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const appPassword = getAppPassword();
  if (appPassword) {
    config.headers['x-app-password'] = appPassword;
  }
  return config;
});

axios.interceptors.response.use(undefined, (error) => {
  const code = error.response?.status === 401 ? error.response.data?.error : null;
  // Only a request that carried a session can have its session end — a
  // first visit with no session is just not signed in yet.
  if ((code === 'SESSION_ENDED' || code === 'SIGN_IN_REQUIRED') && error.config?.headers?.Authorization) {
    setSessionToken(null);
    window.dispatchEvent(new CustomEvent(SIGNED_OUT_EVENT, { detail: { code } }));
  }
  return Promise.reject(error);
});
